import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { useAssistantPulseQuery } from '@/hooks/useAssistantProactive';
import { useIsAssistantAvailable } from '@/hooks/useIsAssistantAvailable';
import { cn } from '@/lib/utils';
import { AssistantAttentionBadge } from './AssistantAttentionBadge';
import { useAssistantPanel } from './assistantPanelContext';

interface AssistantLauncherProps {
  isCompact: boolean;
}

export function AssistantLauncher({ isCompact }: AssistantLauncherProps) {
  const { t } = useTranslation('assistant');
  const isAvailable = useIsAssistantAvailable();
  const { isOpen, toggle } = useAssistantPanel();
  const pulseQuery = useAssistantPulseQuery();
  if (isAvailable === false) {
    return null;
  }
  const attentionCount =
    pulseQuery.data !== undefined && pulseQuery.data.isActive ? pulseQuery.data.attentionTotal : 0;
  const showsAttention = attentionCount > 0 && isOpen === false;
  const accessibleLabel = showsAttention
    ? t('launcher.labelWithAttention', { count: attentionCount })
    : t('launcher.label');
  if (isCompact) {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={accessibleLabel}
        aria-pressed={isOpen}
        className={cn(
          'relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors',
          isOpen ? 'bg-zooprimary text-zoowhite' : 'bg-zoovpalelavender text-zooprimary'
        )}
      >
        <Icon name="Sparkles" />
        {showsAttention ? <AssistantAttentionBadge count={attentionCount} /> : null}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={isOpen}
      aria-label={showsAttention ? accessibleLabel : undefined}
      className={cn(
        'relative h-10 pl-4 pr-2 flex items-center gap-2 rounded-full border text-sm font-semibold shadow-sm transition-colors',
        isOpen
          ? 'bg-zooprimary border-zooprimary text-zoowhite'
          : 'bg-zoowhite border-zooborder text-zooindigo hover:bg-zoovpalelavender'
      )}
    >
      <Icon name="Sparkles" className={isOpen ? 'text-zoowhite' : 'text-zooprimary'} />
      {t('launcher.label')}
      <kbd
        className={cn(
          'ml-1 text-[10px] font-semibold rounded-md px-1.5 py-0.5',
          isOpen ? 'bg-zoowhite/20 text-zoowhite' : 'bg-zoovpalelavender text-zoomuted'
        )}
      >
        {t('launcher.shortcut')}
      </kbd>
      {showsAttention ? <AssistantAttentionBadge count={attentionCount} /> : null}
    </button>
  );
}
